"use client";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { PopupButton } from "react-calendly";
import posthog from "posthog-js";
import {
  calendlyUrlWithBookingContext,
  consentedAnonymousDistinctId,
  prepareBookingContext,
} from "@/library/booking-context";

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CALENDLY_URL = "https://calendly.com/topgeo-sales/30min";

function readyDistinctId(): string | null | undefined { return consentedAnonymousDistinctId(posthog); }

export default function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(CALENDLY_URL);
  const openAttempt = useRef(0);

  useEffect(() => {
    setMounted(true);
    void prepareBookingContext({ getDistinctId: readyDistinctId }).then((context) => {
      if (context) setCalendlyUrl(calendlyUrlWithBookingContext(CALENDLY_URL, context));
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !mounted) return;
    const attempt = ++openAttempt.current;
    let cancelled = false;
    const open = async () => {
      // Wait only for the same bounded request used elsewhere. A fresh token is
      // used when available; failure still opens the unchanged Calendly URL.
      const context = await prepareBookingContext({ getDistinctId: readyDistinctId });
      if (cancelled || attempt !== openAttempt.current) return;
      const freshUrl = context
        ? calendlyUrlWithBookingContext(CALENDLY_URL, context)
        : CALENDLY_URL;
      // Force the PopupButton prop to the freshly resolved URL before the
      // synthetic click. The request itself remains bounded and failures use
      // Calendly's original URL.
      requestAnimationFrame(() => {
        if (cancelled || attempt !== openAttempt.current) return;
        flushSync(() => setCalendlyUrl(freshUrl));
        wrapperRef.current?.querySelector<HTMLButtonElement>("button")?.click();
        onClose();
      });
    };
    void open();
    return () => { cancelled = true; };
  }, [isOpen, mounted, onClose]);

  if (!mounted) return null;

  return (
    <div
      ref={wrapperRef}
      style={{ position: "absolute", visibility: "hidden", pointerEvents: "none", width: 0, height: 0, overflow: "hidden" }}
    >
      <PopupButton
        url={calendlyUrl}
        rootElement={document.body}
        text="open"
      />
    </div>
  );
}

/*
// ─── Original custom form modal (kept for reference) ───────────────────────

import { z } from "zod";
import { emailSchema, phoneSchema, nameSchema, companySchema } from "@/library/schemas";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface BookDemoModalPropsOld {
  isOpen: boolean;
  onClose: () => void;
}

function BookDemoModalOld({ isOpen, onClose }: BookDemoModalPropsOld) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    preferredDate: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, "") }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const isFormValid =
    !!formData.name && !!formData.email && !!formData.phone &&
    !!formData.company && !!formData.preferredDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      if (e.data?.event !== "calendly.event_scheduled") return;
      const formSchema = z.object({
        name: nameSchema,
        email: emailSchema,
        phone: phoneSchema,
        company: companySchema,
        preferredDate: z.string().min(1, "Please select a preferred date"),
      });
      const result = formSchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.issues.forEach((issue) => {
          if (issue.path[0]) fieldErrors[issue.path[0].toString()] = issue.message;
        });
        setErrors(fieldErrors);
        return;
      }
      setErrors({});
      setIsLoading(true);
      setMessage("");
      try {
        setMessage("Demo booked successfully! We'll contact you soon. 🎉");
        setTimeout(() => {
          setFormData({ name: "", email: "", phone: "", company: "", preferredDate: "" });
        }, 2000);
      } catch (error) {
        console.error(error);
        setMessage("Something went wrong");
      } finally {
        setIsLoading(false);
      }
    };
    window.addEventListener("message", handleCalendlyEvent);
    return () => window.removeEventListener("message", handleCalendlyEvent);
  }, [isFormValid, onClose]);

  const handleClose = () => {
    setFormData({ name: "", email: "", phone: "", company: "", preferredDate: "" });
    setMessage("");
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface-raised rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold font-raleway text-ink">Book a Demo</h2>
          <button onClick={handleClose} className="text-ink-faint hover:text-ink-muted text-2xl cursor-pointer">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {(["name", "email", "phone", "company"] as const).map(field => (
            <div key={field}>
              <label htmlFor={field} className="block text-sm font-medium text-ink-muted mb-1 capitalize">
                {field} <span className="text-red-500 text-lg">*</span>
              </label>
              <input
                type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                id={field} name={field}
                value={formData[field]}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 text-ink border ${errors[field] ? "border-red-500" : "border-hairline-strong"} rounded-md focus:outline-none focus:ring-2 focus:ring-ink`}
                placeholder={`Enter your ${field}`}
                required disabled={isLoading}
              />
              {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
            </div>
          ))}
          <div>
            <label htmlFor="preferredDate" className="block text-sm font-medium text-ink-muted mb-1">
              Preferred Date <span className="text-red-500 text-lg">*</span>
            </label>
            <input
              type="date" id="preferredDate" name="preferredDate"
              value={formData.preferredDate} onChange={handleInputChange}
              className={`w-full px-3 py-2 text-ink border ${errors.preferredDate ? "border-red-500" : "border-hairline-strong"} rounded-md focus:outline-none focus:ring-2 focus:ring-ink`}
              required disabled={isLoading} min={new Date().toISOString().split("T")[0]}
            />
            {errors.preferredDate && <p className="text-red-500 text-xs mt-1">{errors.preferredDate}</p>}
          </div>
          {message && (
            <div className={`p-3 rounded-md text-sm ${message.includes("successfully") ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
              {message}
            </div>
          )}
          <button type="submit" disabled={isLoading}
            className="w-full bg-ink text-surface py-2 px-4 rounded-md font-semibold hover:bg-ink/90 transition-colors disabled:opacity-50 cursor-pointer">
            {isLoading ? "Booking Demo..." : "Book Demo"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
*/
